/* Table screens: Invoices, Expenses, Payments, Employees */
(function () {
  const { money, fmtDate } = window.AC;

  function markdownCells(line) {
    const trimmed = String(line || '').trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map((cell) => cell.trim());
  }

  function isMarkdownTableSeparator(line) {
    return markdownCells(line).length > 1 && markdownCells(line).every((cell) => /^:?-{3,}:?$/.test(cell));
  }

  function renderMarkdownInline(value, keyPrefix) {
    const text = String(value || '');
    const tokenPattern = /(\[[^\]\n]+\]\((?:https?:\/\/|mailto:)[^) \n]+\)|`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_)/g;
    const nodes = [];
    let lastIndex = 0;
    let match;
    while ((match = tokenPattern.exec(text)) !== null) {
      const token = match[0];
      if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
      const key = `${keyPrefix}-${match.index}`;
      if (token.startsWith('`')) {
        nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
      } else if (token.startsWith('**') || token.startsWith('__')) {
        nodes.push(<strong key={key}>{renderMarkdownInline(token.slice(2, -2), key)}</strong>);
      } else if (token.startsWith('*') || token.startsWith('_')) {
        nodes.push(<em key={key}>{renderMarkdownInline(token.slice(1, -1), key)}</em>);
      } else {
        const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        nodes.push(
          <a key={key} href={link[2]} target="_blank" rel="noreferrer">
            {renderMarkdownInline(link[1], key)}
          </a>
        );
      }
      lastIndex = match.index + token.length;
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    return nodes.length ? nodes : text;
  }

  function renderMarkdownLines(lines, keyPrefix) {
    return lines.flatMap((line, index) => {
      const nodes = renderMarkdownInline(line, `${keyPrefix}-${index}`);
      return index === 0 ? [nodes] : [<br key={`${keyPrefix}-br-${index}`} />, nodes];
    });
  }

  function AiChatMarkdown({ text }) {
    const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
    const blocks = [];
    let index = 0;
    const isBlockStart = (line, nextLine) => {
      const trimmed = line.trim();
      return /^```/.test(trimmed)
        || /^#{1,4}\s+/.test(trimmed)
        || /^>\s?/.test(trimmed)
        || /^[-*]\s+/.test(trimmed)
        || /^\d+\.\s+/.test(trimmed)
        || (line.includes('|') && nextLine && isMarkdownTableSeparator(nextLine));
    };

    while (index < lines.length) {
      const line = lines[index];
      const trimmed = line.trim();
      const key = `md-${blocks.length}`;
      if (!trimmed) {
        index += 1;
        continue;
      }
      if (/^```/.test(trimmed)) {
        const code = [];
        index += 1;
        while (index < lines.length && !/^```/.test(lines[index].trim())) {
          code.push(lines[index]);
          index += 1;
        }
        if (index < lines.length) index += 1;
        blocks.push(<pre key={key}><code>{code.join('\n')}</code></pre>);
        continue;
      }
      const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
      if (heading) {
        const Tag = `h${Math.min(4, heading[1].length + 2)}`;
        blocks.push(React.createElement(Tag, { key }, renderMarkdownInline(heading[2], key)));
        index += 1;
        continue;
      }
      if (/^>\s?/.test(trimmed)) {
        const quote = [];
        while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
          quote.push(lines[index].trim().replace(/^>\s?/, ''));
          index += 1;
        }
        blocks.push(<blockquote key={key}>{renderMarkdownLines(quote, key)}</blockquote>);
        continue;
      }
      if (/^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
        const ordered = /^\d+\.\s+/.test(trimmed);
        const items = [];
        const itemPattern = ordered ? /^\d+\.\s+/ : /^[-*]\s+/;
        while (index < lines.length && itemPattern.test(lines[index].trim())) {
          items.push(lines[index].trim().replace(itemPattern, ''));
          index += 1;
        }
        const ListTag = ordered ? 'ol' : 'ul';
        blocks.push(React.createElement(ListTag, { key }, items.map((item, itemIndex) => (
          <li key={`${key}-li-${itemIndex}`}>{renderMarkdownInline(item, `${key}-li-${itemIndex}`)}</li>
        ))));
        continue;
      }
      if (line.includes('|') && lines[index + 1] && isMarkdownTableSeparator(lines[index + 1])) {
        const header = markdownCells(line);
        const rows = [];
        index += 2;
        while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
          rows.push(markdownCells(lines[index]));
          index += 1;
        }
        blocks.push(
          <div className="ai-chat-table-wrap" key={key}>
            <table>
              <thead><tr>{header.map((cell, cellIndex) => <th key={`${key}-h-${cellIndex}`}>{renderMarkdownInline(cell, `${key}-h-${cellIndex}`)}</th>)}</tr></thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={`${key}-r-${rowIndex}`}>{header.map((_, cellIndex) => <td key={`${key}-r-${rowIndex}-${cellIndex}`}>{renderMarkdownInline(row[cellIndex] || '', `${key}-r-${rowIndex}-${cellIndex}`)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
      const paragraph = [];
      while (index < lines.length && lines[index].trim() && !isBlockStart(lines[index], lines[index + 1])) {
        paragraph.push(lines[index]);
        index += 1;
      }
      blocks.push(<p key={key}>{renderMarkdownLines(paragraph, key)}</p>);
    }

    return <div className="ai-chat-markdown">{blocks}</div>;
  }

  function DataTable({ columns, rows, rowKey, empty, footer, maxHeight, getRowClassName, onRowClick, actions }) {
    const list = rows || [];
    const activeActions = actions && actions.length ? actions : null;
    const totalColumns = columns.length + (activeActions ? 1 : 0);
    const wrapStyle = maxHeight ? { maxHeight } : null;
    return (
      <div className="tbl-wrap" style={wrapStyle}>
        <table className="tbl">
          {columns.some((col) => col.header) && (
            <thead><tr>
              {columns.map((col) => <th key={col.key || col.header} className={col.headerClassName || col.className || ''} style={col.style}>{col.header}</th>)}
              {activeActions && <th style={{ width: 1 }}></th>}
            </tr></thead>
          )}
          <tbody>
            {list.map((row, index) => {
              const rowClass = getRowClassName ? getRowClassName(row, index) : '';
              const clickable = onRowClick ? 'clickable' : '';
              return (
                <tr key={rowKey ? rowKey(row, index) : index} className={[rowClass, clickable].filter(Boolean).join(' ')} onClick={onRowClick ? () => onRowClick(row, index) : undefined}>
                  {columns.map((col) => (
                    <td key={col.key || col.header} className={typeof col.className === 'function' ? col.className(row, index) : col.className || ''} style={typeof col.cellStyle === 'function' ? col.cellStyle(row, index) : col.cellStyle}>
                      {col.render ? col.render(row, index) : row[col.key]}
                    </td>
                  ))}
                  {activeActions && (
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {activeActions.filter((action) => !action.show || action.show(row, index)).map((action) => (
                          <button
                            key={action.key || action.title || action.label}
                            className={action.className || (action.label ? 'btn sm ghost' : 'icon-btn')}
                            title={action.title}
                            disabled={action.disabled ? action.disabled(row, index) : false}
                            onClick={(e) => { e.stopPropagation(); action.onClick(row, index); }}
                          >
                            {action.icon && <Icon name={action.icon} size={action.label ? 14 : 15} />}
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={totalColumns}><Empty>{empty || 'No records.'}</Empty></td></tr>}
          </tbody>
          {list.length > 0 && footer && <tfoot>{footer}</tfoot>}
        </table>
      </div>
    );
  }

  function StatusFilter({ value, onChange, options }) {
    return <Segmented value={value} onChange={onChange} options={options} />;
  }

  function Invoices({ db, query, onDrawer, onAction }) {
    const [status, setStatus] = React.useState('all');
    const rows = db.invoices.map((iv) => MODEL.enrich(db, iv))
      .filter((e) => {
        if (status === 'outstanding' && !e.open) return false;
        if (status === 'overdue' && !e.overdue) return false;
        if (status === 'draft' && e.invoice.status !== 'Draft') return false;
        if (status === 'paid' && e.invoice.status !== 'Paid') return false;
        if (query) {
          const q = query.toLowerCase();
          if (!(e.invoice.number.toLowerCase().includes(q) || (e.customer && e.customer.name.toLowerCase().includes(q)))) return false;
        }
        return true;
      })
      .sort((a, b) => b.invoice.issueDate.localeCompare(a.invoice.issueDate));

    const sum = rows.reduce((a, e) => ({ total: a.total + e.totals.total, paid: a.paid + e.totals.paid, out: a.out + e.totals.outstanding }), { total: 0, paid: 0, out: 0 });

    return (
      <div>
        <Toolbar>
          <StatusFilter value={status} onChange={setStatus} options={[
            { value: 'all', label: 'All' }, { value: 'outstanding', label: 'Outstanding' }, { value: 'overdue', label: 'Overdue' }, { value: 'draft', label: 'Draft' }, { value: 'paid', label: 'Paid' },
          ]} />
          <span className="muted" style={{ fontSize: 12 }}>{rows.length} invoices</span>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn" onClick={() => onDrawer('invoice')}><Icon name="plus" size={14} />New invoice</button>
          </div>
        </Toolbar>
        <Panel flush>
          <DataTable
            rows={rows}
            rowKey={(e) => e.invoice.id}
            empty="No invoices match this filter."
            columns={[
              { header: 'Invoice', render: (e) => <div className="cellstack"><span className="t-strong mono">{e.invoice.number}</span><span className="t-sub">{e.invoice.lines[0] ? e.invoice.lines[0].description : ''}</span></div> },
              { header: 'Customer', render: (e) => e.customer ? e.customer.name : '—' },
              { header: 'Issue', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (e) => fmtDate(e.invoice.issueDate) },
              { header: 'Due', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (e) => <React.Fragment>{fmtDate(e.invoice.dueDate)}{e.overdue && <span className="neg" style={{ fontSize: 10, marginLeft: 4 }}>+{e.overdueDays}d</span>}</React.Fragment> },
              { header: 'Status', render: (e) => <Pill kind={e.display} sm /> },
              { header: 'Total', className: 'num', render: (e) => money(e.totals.total) },
              { header: 'Paid', className: 'num muted', render: (e) => e.totals.paid ? money(e.totals.paid) : '—' },
              { header: 'Outstanding', className: 'num t-strong', cellStyle: (e) => e.totals.outstanding > 0 ? { color: e.overdue ? 'var(--neg)' : 'var(--ink)' } : { color: 'var(--faint)' }, render: (e) => e.totals.outstanding > 0 ? money(e.totals.outstanding) : '—' },
            ]}
            actions={[
              { key: 'finalize', label: 'Finalize', show: (e) => e.invoice.status === 'Draft', onClick: (e) => onAction('finalize', e.invoice) },
              { key: 'pay', label: 'Mark paid', show: (e) => e.open, onClick: (e) => onAction('pay', e.invoice) },
              { key: 'edit', title: 'Edit', icon: 'edit', show: (e) => e.invoice.status === 'Draft', onClick: (e) => onDrawer('invoice', e.invoice.id) },
              { key: 'delete', title: 'Delete invoice', icon: 'trash', show: (e) => e.invoice.status === 'Draft', onClick: (e) => onAction('delete', e.invoice) },
              { key: 'open', title: 'Open invoice', icon: 'external', onClick: (e) => onAction('print', e.invoice) },
            ]}
            footer={<tr><td colSpan={5}>{rows.length} invoices</td><td className="num">{money(sum.total)}</td><td className="num pos">{money(sum.paid)}</td><td className="num">{money(sum.out)}</td><td></td></tr>}
          />
        </Panel>
      </div>
    );
  }

  function Expenses({ db, query, onDrawer, onAction }) {
    const [cat, setCat] = React.useState('all');
    const projectById = new Map((db.projects || []).map((project) => [project.id, project]));
    const projectName = (expense) => {
      const project = expense.projectId && projectById.get(expense.projectId);
      return project ? project.name : '';
    };
    let list = db.expenses.slice().sort((a, b) => b.date.localeCompare(a.date));
    if (cat === 'recurring') list = list.filter((x) => x.recurrenceFrequency && x.recurrenceFrequency !== 'None');
    if (cat === 'wages') list = list.filter((x) => x.isWage);
    if (cat === 'oneoff') list = list.filter((x) => !x.recurrenceFrequency || x.recurrenceFrequency === 'None');
    if (query) { const q = query.toLowerCase(); list = list.filter((x) => x.category.toLowerCase().includes(q) || x.vendor.toLowerCase().includes(q) || x.description.toLowerCase().includes(q) || projectName(x).toLowerCase().includes(q)); }
    const sum = list.reduce((a, x) => ({ net: a.net + x.amount, tax: a.tax + x.taxAmount }), { net: 0, tax: 0 });

    return (
      <div>
        <Toolbar>
          <StatusFilter value={cat} onChange={setCat} options={[
            { value: 'all', label: 'All' }, { value: 'recurring', label: 'Recurring' }, { value: 'wages', label: 'Wages' }, { value: 'oneoff', label: 'One-off' },
          ]} />
          <span className="muted" style={{ fontSize: 12 }}>{list.length} entries</span>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn" onClick={() => onDrawer('expense')}><Icon name="plus" size={14} />Record expense</button>
          </div>
        </Toolbar>
        <Panel flush>
          <DataTable
            rows={list}
            rowKey={(x) => x.id}
            empty="No expenses match this filter."
            columns={[
              { header: 'Date', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (x) => fmtDate(x.date) },
              { header: 'Category', render: (x) => <React.Fragment><span className="t-strong">{x.category}</span>{x.isWage && <span className="pill partial sm" style={{ marginLeft: 6 }}><span className="dot" />Wage</span>}</React.Fragment> },
              { header: 'Project', render: (x) => projectName(x) ? <span className="chip"><Icon name="projects" size={11} />{projectName(x)}</span> : <span className="faint">Company</span> },
              { header: 'Vendor', key: 'vendor' },
              { header: 'Description', className: 'muted', cellStyle: { maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, key: 'description' },
              { header: 'Recurrence', render: (x) => x.recurrenceFrequency && x.recurrenceFrequency !== 'None' ? <span className="chip"><Icon name="refresh" size={11} />{x.recurrenceFrequency}</span> : <span className="faint">One-off</span> },
              { header: 'Net', className: 'num', render: (x) => money(x.amount) },
              { header: 'Tax', className: 'num muted', render: (x) => x.taxAmount ? money(x.taxAmount) : '—' },
              { header: 'Total', className: 'num t-strong', render: (x) => money(x.amount + x.taxAmount) },
            ]}
            actions={[
              { key: 'doc', title: 'Document attached', icon: 'doc', show: (x) => !!x.document, onClick: (x) => onAction('doc', x) },
              { key: 'edit', title: 'Edit', icon: 'edit', onClick: (x) => onDrawer('expense', x.id) },
              { key: 'delete', title: 'Delete expense', icon: 'trash', show: (x) => !(x.payments || []).length, onClick: (x) => onAction('deleteExpense', x) },
            ]}
            footer={<tr><td colSpan={6}>{list.length} entries</td><td className="num">{money(sum.net)}</td><td className="num muted">{money(sum.tax)}</td><td className="num">{money(sum.net + sum.tax)}</td><td></td></tr>}
          />
        </Panel>
      </div>
    );
  }

  function Payments({ db, query, onDrawer, onAction }) {
    const [status, setStatus] = React.useState('open');
    const projectById = new Map((db.projects || []).map((project) => [project.id, project]));
    const projectName = (expense) => {
      const project = expense.projectId && projectById.get(expense.projectId);
      return project ? project.name : '';
    };
    let rows = MODEL.expensePaymentRows(db);
    if (status === 'open') rows = rows.filter((row) => !row.paid);
    if (status === 'paid') rows = rows.filter((row) => row.paid);
    if (query) {
      const q = query.toLowerCase();
      rows = rows.filter((row) => row.expense.category.toLowerCase().includes(q) || row.expense.vendor.toLowerCase().includes(q) || row.expense.description.toLowerCase().includes(q) || projectName(row.expense).toLowerCase().includes(q));
    }

    const due = rows.filter((row) => !row.paid).reduce((sum, row) => sum + row.amount, 0);
    const paid = rows.filter((row) => row.paid).reduce((sum, row) => sum + row.amount, 0);

    return (
      <div>
        <Toolbar>
          <StatusFilter value={status} onChange={setStatus} options={[
            { value: 'open', label: 'Open' }, { value: 'paid', label: 'Paid' }, { value: 'all', label: 'All' },
          ]} />
          <span className="muted" style={{ fontSize: 12 }}>{rows.length} payments</span>
        </Toolbar>
        <Panel flush>
          <DataTable
            rows={rows}
            rowKey={(row) => `${row.expense.id}-${row.occurrenceDate}`}
            empty="No payable expense items match this filter."
            columns={[
              { header: 'Due', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (row) => fmtDate(row.occurrenceDate) },
              { header: 'Expense', render: (row) => <div className="cellstack"><span className="t-strong">{row.expense.category}</span><span className="t-sub">{row.expense.description}</span></div> },
              { header: 'Project', render: (row) => projectName(row.expense) ? <span className="chip"><Icon name="projects" size={11} />{projectName(row.expense)}</span> : <span className="faint">Company</span> },
              { header: 'Vendor', render: (row) => row.expense.vendor || '—' },
              { header: 'Recurrence', render: (row) => row.expense.recurrenceFrequency && row.expense.recurrenceFrequency !== 'None' ? <span className="chip"><Icon name="refresh" size={11} />{row.expense.recurrenceFrequency}</span> : <span className="faint">One-off</span> },
              { header: 'Status', render: (row) => row.paid ? <Pill kind="Paid" sm /> : <Pill kind="Open" sm /> },
              { header: 'Paid on', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (row) => row.payment ? fmtDate(row.payment.paidOn) : '—' },
              { header: 'Amount', className: 'num t-strong', render: (row) => money(row.amount) },
            ]}
            actions={[
              { key: 'pay', label: 'Mark paid', show: (row) => !row.paid, onClick: (row) => onAction('payExpenseOccurrence', row) },
              { key: 'edit', title: 'Edit expense', icon: 'edit', onClick: (row) => onDrawer('expense', row.expense.id) },
            ]}
            footer={<tr><td colSpan={7}>Open {money(due)} · Paid {money(paid)}</td><td className="num">{money(due + paid)}</td><td></td></tr>}
          />
        </Panel>
      </div>
    );
  }

  function Employees({ db, query, onDrawer, onAction }) {
    let list = db.employees.slice();
    if (query) { const q = query.toLowerCase(); list = list.filter((e) => e.name.toLowerCase().includes(q) || (e.role || '').toLowerCase().includes(q) || (e.description || '').toLowerCase().includes(q)); }
    const isAi = (employee) => (employee.type || 'Human') === 'Ai';
    const wageRows = MODEL.expensePaymentRows(db).filter((row) => row.expense.isWage && row.expense.employeeId);
    const currentWageExpense = (employee) => {
      const period = MODEL.currentWagePeriod(employee);
      return period ? db.expenses.find((expense) => expense.isWage && expense.employeeId === employee.id && expense.wagePeriodId === period.id) : null;
    };
    const wageSummary = (employee) => {
      const rows = wageRows.filter((row) => row.expense.employeeId === employee.id);
      return {
        open: rows.filter((row) => !row.paid).reduce((sum, row) => sum + row.amount, 0),
        paid: rows.filter((row) => row.paid).reduce((sum, row) => sum + row.amount, 0),
        count: db.expenses.filter((expense) => expense.isWage && expense.employeeId === employee.id).length,
      };
    };
    const totalMonthly = list.filter((e) => e.active && !isAi(e)).reduce((s, e) => s + MODEL.employeeMonthlyWage(e), 0);
    const activeHumans = list.filter((e) => e.active && !isAi(e)).length;
    const activeAi = list.filter((e) => e.active && isAi(e)).length;

    return (
      <div>
        <Toolbar>
          <span className="muted" style={{ fontSize: 12 }}>{activeHumans} human · {activeAi} AI · {list.length} total</span>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn" onClick={() => onDrawer('employee')}><Icon name="plus" size={14} />Add employee</button>
          </div>
        </Toolbar>
        <Panel flush>
          <DataTable
            rows={list}
            rowKey={(e) => e.id}
            columns={[
              { header: 'Name', render: (e) => <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Avatar name={e.name} avatarKey={e.avatarKey} /><span className="cellstack"><span className="t-strong">{e.name}</span>{isAi(e) && e.description ? <span className="t-sub">{e.description}</span> : null}</span></div> },
              { header: 'Type', render: (e) => <Pill kind={isAi(e) ? 'Open' : 'Draft'}>{isAi(e) ? 'AI' : 'Human'}</Pill> },
              { header: 'Gender', render: (e) => e.gender || 'Male' },
              { header: 'Role', key: 'role' },
              { header: 'Email', className: 'muted', render: (e) => isAi(e) ? <span className="faint">Not required</span> : e.email },
              { header: 'Status', render: (e) => <Pill kind={e.active ? 'Active' : 'Inactive'} sm /> },
              { header: 'Wage', className: 'num', render: (e) => isAi(e) ? <span className="faint">—</span> : money(e.wageAmount) },
              { header: 'Frequency', className: 'muted', render: (e) => isAi(e) ? <span className="faint">—</span> : e.wageFrequency },
              { header: 'Monthly cost', className: 'num', render: (e) => e.active && !isAi(e) ? money(MODEL.employeeMonthlyWage(e)) : <span className="faint">—</span> },
              { header: 'Wage payments', render: (e) => {
                if (isAi(e)) return <span className="faint">No payroll</span>;
                const summary = wageSummary(e);
                return summary.count ? <div className="cellstack"><span className="t-strong">{money(summary.paid)} paid</span><span className="t-sub">{summary.open ? `${money(summary.open)} open` : 'No open payments'}</span></div> : <span className="faint">No linked payments</span>;
              } },
              { header: 'Capabilities', render: (e) => {
                if (!isAi(e)) return <span className="faint">—</span>;
                const caps = e.codexCapabilities || {};
                const count = (caps.plugins || []).length + (caps.skills || []).length;
                return count ? <span className="t-strong">{count}</span> : <span className="faint">None</span>;
              } },
            ]}
            actions={[
              { key: 'wageExpense', title: 'Open current wage expense', icon: 'wallet', onClick: (e) => {
                const expense = currentWageExpense(e);
                const period = MODEL.currentWagePeriod(e);
                expense ? onDrawer('expense', expense.id) : onDrawer('expense', null, { employeeId: e.id, wagePeriodId: period && period.id });
              }, show: (e) => !isAi(e) },
              { key: 'edit', title: 'Edit', icon: 'edit', onClick: (e) => onDrawer('employee', e.id) },
              { key: 'delete', title: 'Delete employee', icon: 'trash', onClick: (e) => onAction('deleteEmployee', e) },
            ]}
            footer={<tr><td colSpan={8}>Active monthly payroll</td><td className="num">{money(totalMonthly)}</td><td></td><td></td><td></td></tr>}
          />
        </Panel>
      </div>
    );
  }

  function ImplementationGuidelines({ db, query, onToast, onError, onReload }) {
    const empty = { title: '', body: '', source: '', description: '' };
    const [kind, setKind] = React.useState('implementation');
    const [drawer, setDrawer] = React.useState(null);
    const [form, setForm] = React.useState(empty);
    const categories = [
      {
        id: 'implementation',
        label: 'Implementation',
        icon: 'doc',
        sub: 'Agent implementation context',
        empty: 'No implementation guideline templates.',
        createLabel: 'Add implementation guideline',
        drawerSub: 'Additional context for project planning and implementation agents',
        placeholder: 'Write the implementation guidance agents should follow.',
        templates: db.implementationGuidelineTemplates || [],
        usageLabel: 'Scopes',
        usageCount: (templateId) => (db.projects || []).reduce((sum, project) => sum
          + ((project.implementationGuidelineTemplateIds || []).includes(templateId) ? 1 : 0)
          + (project.workPackages || []).filter((workPackage) => (workPackage.implementationGuidelineTemplateIds || []).includes(templateId)).length, 0),
        create: window.API.createImplementationGuidelineTemplate,
        update: window.API.updateImplementationGuidelineTemplate,
        remove: window.API.deleteImplementationGuidelineTemplate,
      },
      {
        id: 'design',
        label: 'Design systems',
        icon: 'fileImage',
        sub: 'Stored markdown design systems',
        empty: 'No stored design systems.',
        createLabel: 'Add design system',
        drawerSub: 'Reusable markdown design-system context for projects and work packages',
        placeholder: 'Write the design system in markdown.',
        templates: db.projectDesignGuidelineTemplates || [],
        usageLabel: 'Scopes',
        usageCount: (templateId) => (db.projects || []).reduce((sum, project) => sum
          + ((project.designGuidelineTemplateIds || []).includes(templateId) ? 1 : 0)
          + (project.workPackages || []).filter((workPackage) => (workPackage.designGuidelineTemplateIds || []).includes(templateId)).length, 0),
        create: window.API.createProjectDesignGuidelineTemplate,
        update: window.API.updateProjectDesignGuidelineTemplate,
        remove: window.API.deleteProjectDesignGuidelineTemplate,
      },
      {
        id: 'libraries',
        label: 'Excalidraw library',
        icon: 'fileImage',
        sub: 'Allowed sketch libraries',
        empty: 'No Excalidraw library templates.',
        createLabel: 'Add Excalidraw library',
        drawerSub: 'Reusable Excalidraw library sources that sketch agents may use',
        placeholder: 'Describe when the sketch artist should use this library.',
        templates: db.excalidrawLibraryTemplates || [],
        usageLabel: 'Scopes',
        usageCount: (templateId) => (db.projects || []).reduce((sum, project) => sum
          + ((project.excalidrawLibraryTemplateIds || []).includes(templateId) ? 1 : 0)
          + (project.workPackages || []).filter((workPackage) => (workPackage.excalidrawLibraryTemplateIds || []).includes(templateId)).length, 0),
        create: window.API.createExcalidrawLibraryTemplate,
        update: window.API.updateExcalidrawLibraryTemplate,
        remove: window.API.deleteExcalidrawLibraryTemplate,
        bodyLabel: 'Usage notes',
        bodyKey: 'description',
        extraField: 'source',
      },
    ];
    const active = categories.find((item) => item.id === kind) || categories[0];
    const normalized = String(query || '').trim().toLowerCase();
    const rows = active.templates
      .filter((template) => !normalized || `${template.title || ''} ${template.body || ''} ${template.source || ''} ${template.description || ''}`.toLowerCase().includes(normalized))
      .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    const set = (k, v) => setForm((current) => ({ ...current, [k]: v }));
    const closeDrawer = () => {
      setDrawer(null);
      setForm(empty);
    };
    const create = () => {
      setForm(empty);
      setDrawer({ mode: 'create', id: null, kind: active.id });
    };
    const edit = (template) => {
      setForm({ title: template.title || '', body: template.body || '', source: template.source || '', description: template.description || '' });
      setDrawer({ mode: 'edit', id: template.id, kind: active.id });
    };
    const save = async () => {
      const drawerCategory = categories.find((item) => item.id === (drawer && drawer.kind)) || active;
      try {
        if (drawer && drawer.id) await drawerCategory.update(drawer.id, form);
        else await drawerCategory.create(form);
        if (onToast) onToast(drawer && drawer.id ? 'Template updated' : 'Template created');
        closeDrawer();
        if (onReload) await onReload();
      } catch (error) {
        if (onError) onError(error);
      }
    };
    const remove = async (template) => {
      if (!window.confirm(`Delete ${template.title || 'this guideline template'}?`)) return;
      try {
        await active.remove(template.id);
        if (onToast) onToast('Template deleted');
        if (drawer && drawer.id === template.id) closeDrawer();
        if (onReload) await onReload();
      } catch (error) {
        if (onError) onError(error);
      }
    };

    return (
      <React.Fragment>
        <Toolbar>
          <span className="muted" style={{ fontSize: 12 }}>{rows.length} {active.label.toLowerCase()} guideline template{rows.length === 1 ? '' : 's'}</span>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn" onClick={create}><Icon name="plus" size={14} />{active.createLabel}</button>
          </div>
        </Toolbar>
        <div className="req-stage">
          <aside className="req-rail" aria-label="Guideline categories">
            {categories.map((category) => (
              <button key={category.id} className={active.id === category.id ? 'on' : ''} onClick={() => setKind(category.id)}>
                <span className="req-rail-ico"><Icon name={category.icon} size={15} /></span>
                <span className="req-rail-copy"><strong>{category.label}</strong><small>{category.sub}</small></span>
                <span className="pd-tabcount">{category.templates.length}</span>
              </button>
            ))}
          </aside>
          <Panel flush>
            <DataTable
              rows={rows}
              rowKey={(template) => template.id}
              empty={active.empty}
              columns={[
                { header: 'Template', render: (template) => {
                  const summary = template.source
                    ? `${template.source}${template.description ? ` - ${template.description}` : ''}`
                    : template.body || '';
                  return <span className="cellstack"><span className="t-strong">{template.title}</span><span className="t-sub">{summary.slice(0, 140)}{summary.length > 140 ? '...' : ''}</span></span>;
                } },
                { header: active.usageLabel, className: 'num', render: (template) => active.usageCount(template.id) },
                { header: 'Updated', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (template) => fmtDate(template.updatedAt) },
              ]}
              actions={[
                { icon: 'edit', title: 'Edit', onClick: edit },
                { icon: 'trash', title: 'Delete', onClick: remove },
              ]}
            />
          </Panel>
        </div>
        {drawer && (
          <React.Fragment>
            <div className="overlay" onClick={closeDrawer} />
            <aside className="drawer">
              <header>
                <div style={{ flex: 1 }}>
                  <h2>{drawer.id ? 'Edit guideline' : 'Create guideline'}</h2>
                  <div className="sub">{(categories.find((item) => item.id === drawer.kind) || active).drawerSub}</div>
                </div>
                <button className="icon-btn" onClick={closeDrawer}><Icon name="close" size={17} /></button>
              </header>
              <div className="body">
                <div className="field"><label>Title</label><input className="inp" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Repository workflow, Definition of done..." /></div>
                {((categories.find((item) => item.id === drawer.kind) || active).extraField === 'source') && (
                  <div className="field"><label>Library source</label><input className="inp mono" value={form.source} onChange={(e) => set('source', e.target.value)} placeholder="g-script/forms.excalidrawlib" /></div>
                )}
                <div className="field"><label>{(categories.find((item) => item.id === drawer.kind) || active).bodyLabel || 'Guideline text'}</label><textarea className="inp" rows="14" value={form[(categories.find((item) => item.id === drawer.kind) || active).bodyKey || 'body']} onChange={(e) => set((categories.find((item) => item.id === drawer.kind) || active).bodyKey || 'body', e.target.value)} placeholder={(categories.find((item) => item.id === drawer.kind) || active).placeholder} /></div>
              </div>
              <footer>
                <button className="btn ghost" onClick={closeDrawer}>Cancel</button>
                <button className="btn" onClick={save}><Icon name="check" size={14} />{drawer.id ? 'Save changes' : 'Create'}</button>
              </footer>
            </aside>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  Object.assign(window, { DataTable, Invoices, Expenses, Payments, Employees, AiChatMarkdown, ImplementationGuidelines });
})();
